import { useFetch } from "@/lib/content";
import { useSEO } from "@/lib/seo";
import { Eyebrow, Reveal, ParallaxImage, CtaButton } from "@/components/site/Motion";
import RichText from "@/components/site/RichText";
import LogoGrid from "@/components/site/LogoGrid";

const ABOUT_IMG =
  "https://images.unsplash.com/photo-1758630737900-a28682c5aa69?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA4Mzl8MHwzfHxtb2Rlcm4lMjBjb3Jwb3JhdGUlMjBvZmZpY2UlMjBpbnRlcmlvcnxlbnwwfHx8fDE3OTA0Nzg2ODZ8MA&ixlib=rb-4.1.0&q=85";

const PRINCIPLES = [
  {
    n: "01",
    title: "Diagnose before prescribing",
    body: "We map what actually happens — not what the org chart says happens — before we recommend a single change.",
  },
  {
    n: "02",
    title: "Hands-on, not advisory-only",
    body: "Our team works in your meetings, with your managers, on your documents. Adoption is the deliverable.",
  },
  {
    n: "03",
    title: "Leave the machine running",
    body: "An engagement succeeds when the cadence, processes and dashboards keep working long after we've left the room.",
  },
];

export default function About() {
  const { data: team } = useFetch("/team");
  const { data: clients } = useFetch("/clients");
  const byGroup = (g) => (clients || []).filter((l) => l.group === g);
  const { data: settings } = useFetch("/content/settings");

  useSEO({
    title: "About RenB Solutions — Our Philosophy & Team",
    description: "RenB (Re-Engineering Business Solutions) brings over 150 man-years of experience in business building and organizational re-engineering to SMEs.",
    path: "/about",
  });

  return (
    <div data-testid="about-page">
      <section className="border-b border-ash">
        <div className="mx-auto grid max-w-[1440px] gap-12 px-5 pb-16 pt-28 md:px-10 md:pb-24 md:pt-40 lg:grid-cols-12">
          <div className="lg:col-span-7">
            <Eyebrow>About RenB</Eyebrow>
            <h1 className="mt-8 font-serif text-5xl font-medium leading-[1.02] tracking-tight md:text-7xl">
              We are the operating partner behind ambitious SMEs<span className="text-progress">.</span>
            </h1>
          </div>
          <div className="lg:col-span-5 lg:self-end">
            <p className="text-base leading-relaxed text-obsidian/70 md:text-lg">
              RenB — Re-Engineering Business Solutions — is a Business Process Management company
              headquartered in Mumbai. Our core team brings decades of expertise across business and
              functional domains, helping companies achieve sustainable growth and operational excellence
              by optimizing processes, enabling efficiency, and providing strategic leadership insight.
            </p>
          </div>
        </div>
        <ParallaxImage src={ABOUT_IMG} alt="RenB Solutions office" className="mx-auto aspect-[21/9] max-w-[1440px]" />
      </section>

      <section className="border-b border-ash">
        <div className="mx-auto max-w-[1440px] px-5 py-20 md:px-10 md:py-32">
          <Reveal>
            <Eyebrow>How we work</Eyebrow>
            <h2 className="mt-8 max-w-3xl font-serif text-4xl font-medium leading-[1.08] tracking-tight md:text-6xl">
              Three beliefs we refuse to compromise on.
            </h2>
          </Reveal>
          <div className="mt-14 grid gap-px border border-ash bg-ash md:grid-cols-3">
            {PRINCIPLES.map((p, i) => (
              <Reveal key={p.n} delay={i * 0.08} className="bg-paper p-8 md:p-12">
                <p className="font-serif text-5xl font-medium text-progress">{p.n}</p>
                <h3 className="mt-8 font-serif text-2xl font-semibold tracking-tight md:text-3xl">{p.title}</h3>
                <p className="mt-4 text-[15px] leading-relaxed text-obsidian/65">{p.body}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="border-b border-ash">
        <div className="mx-auto max-w-[1440px] px-5 py-20 md:px-10 md:py-32">
          <Reveal>
            <Eyebrow>The people</Eyebrow>
            <h2 className="mt-8 font-serif text-4xl font-medium tracking-tight md:text-6xl">Leadership</h2>
          </Reveal>
          <div className="mt-14 grid gap-8 md:grid-cols-3">
            {(team || []).map((m, i) => (
              <Reveal key={m.id} delay={i * 0.08} className="border border-ash">
                {m.photo ? (
                  <div className="aspect-[4/5] overflow-hidden bg-obsidian">
                    <img
                      data-testid={`team-photo-${m.id}`}
                      src={m.photo}
                      alt={m.name}
                      loading="lazy"
                      className="h-full w-full object-cover object-top grayscale transition-transform duration-700 ease-out hover:scale-[1.03]"
                    />
                  </div>
                ) : (
                  <div className="flex aspect-[4/5] items-center justify-center bg-obsidian" aria-hidden="true">
                    <span className="font-serif text-7xl font-medium text-paper/90">
                      {m.name.split(" ").map((w) => w[0]).join("").slice(0, 2)}
                    </span>
                  </div>
                )}
                <div className="p-8">
                  <h3 className="font-serif text-2xl font-semibold tracking-tight">{m.name}</h3>
                  <p className="mt-1 text-xs font-bold uppercase tracking-[0.18em] text-progress">{m.role}</p>
                  {m.bio && <p className="mt-4 text-sm leading-relaxed text-obsidian/65">{m.bio}</p>}
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <LogoGrid
        testid="about-clients"
        eyebrow="A glimpse"
        title="RenB clients"
        intro="We are proud to partner with a diverse range of clients across industries. Some of our esteemed clients include:"
        items={byGroup("clients")}
      />
      <LogoGrid
        testid="about-ecosystem"
        eyebrow="Network"
        title="Our ecosystem partners"
        intro="RenB collaborates with a network of esteemed partners to deliver comprehensive and high-quality solutions. Our partners include:"
        items={byGroup("ecosystem")}
      />
      <LogoGrid testid="about-partnered" eyebrow="A glimpse" title="Partnered clients" items={byGroup("partnered")} />

      <section className="bg-paper">
        <div className="mx-auto max-w-[1440px] px-5 py-24 text-center md:px-10 md:py-32">
          <Reveal>
            <h2 className="mx-auto max-w-3xl font-serif text-4xl font-medium leading-tight tracking-tight md:text-6xl">
              {settings?.tagline ? `${settings.tagline}.` : "Where Process Meets Progress."}
            </h2>
            <div className="mt-10">
              <CtaButton testid="about-cta" href="/book">
                Book a Discovery Call
              </CtaButton>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
